import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Breadcrumbs,
  alpha,
  Menu,
  MenuItem,
  Alert,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/ExitToApp';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService, websocketService, UniqCampLogo } from '@uniqcamp/shared';
import { useSelectedSchool } from '../contexts/SelectedSchoolContext';
import { useQuery, useQueryClient } from 'react-query';
import type { StudentsResponse } from '../types';

const DRAWER_WIDTH = 280;

const navItems = [
  { path: '/', label: 'Home', icon: <HomeIcon /> },
  { path: '/students', label: 'Students', icon: <PeopleIcon /> },
  { path: '/dispersals/at-gate', label: 'Dispersal Request', icon: <SchoolIcon /> },
  { path: '/dispersals/early', label: 'Early Request', icon: <ScheduleIcon /> },
  { path: '/dispersals/status', label: 'Pickup Status', icon: <EventAvailableIcon /> },
  { path: '/profile', label: 'Profile', icon: <PersonIcon /> },
];

const pathToLabel: Record<string, string> = {
  '/': 'Home',
  '/students': 'Students',
  '/dispersals': 'Dispersals',
  '/dispersals/at-gate': 'Dispersal Request',
  '/dispersals/early': 'Early Request',
  '/dispersals/status': 'Pickup Status',
  '/profile': 'Profile',
};

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [schoolMenuAnchor, setSchoolMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = authService.getStoredUser();
  const schoolReadOnly = Boolean((user as { schoolReadOnly?: boolean } | null)?.schoolReadOnly);
  const { schools, hasMultipleSchools, selectedSchoolId, setSelectedSchoolId } = useSelectedSchool();
  const selectedSchool = schools.find((s) => s._id === selectedSchoolId);

  const { data: studentsData } = useQuery<StudentsResponse>(
    'parent-students',
    () => apiClient.get('/students').then((r: { data: unknown }) => r.data as StudentsResponse),
    { enabled: !!user }
  );
  const students = React.useMemo(() => studentsData?.students ?? [], [studentsData]);
  const dispersalUpdateCallbackRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!user) return;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return;
    websocketService.connect(token);
    students.forEach((s) => {
      const schoolId = s.schoolId && typeof s.schoolId === 'object' && '_id' in s.schoolId ? s.schoolId._id : undefined;
      if (s._id && schoolId) websocketService.subscribeToDispersal(s._id, schoolId);
    });
    const onUpdate = () => queryClient.invalidateQueries(['dispersals']);
    dispersalUpdateCallbackRef.current = onUpdate;
    websocketService.onDispersalUpdate(onUpdate);
    return () => {
      websocketService.off('dispersal:update', onUpdate);
      dispersalUpdateCallbackRef.current = null;
    };
  }, [user, queryClient, students]);

  const handleLogout = () => {
    authService.logout();
  };

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbLabels = pathSegments.length
    ? [{ label: 'Home', path: '/' }, ...pathSegments.map((segment, i) => {
        const path = '/' + pathSegments.slice(0, i + 1).join('/');
        return { label: pathToLabel[path] || segment, path };
      })]
    : [{ label: 'Home', path: '/' }];

  const drawer = (
    <Box sx={{ py: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0.5 }}>
          <UniqCampLogo style={{ maxHeight: 40, width: 'auto' }} />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Parent Portal
        </Typography>
      </Box>
      <List sx={{ flex: 1, px: 1.5 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isActive}
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  py: 1.25,
                  ...(isActive && {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                    color: 'primary.main',
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                  }),
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: isActive ? 600 : 500 }} />
              </ListItemButton>
            </ListItem>
          );
        })}
        <ListItem disablePadding sx={{ mt: 1 }}>
          <ListItemButton onClick={handleLogout} sx={{ py: 1.25 }} color="inherit">
            <ListItemIcon sx={{ minWidth: 40 }}><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
      {user && (
        <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight={500}>{String(user?.name ?? '')}</Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: DRAWER_WIDTH },
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: { xs: 56, sm: 64 } }}>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 0, display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'inherit' }}>
            {String(user?.name ?? '')}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {hasMultipleSchools && (
            <>
              <Typography
                variant="body2"
                component="button"
                onClick={(e) => setSchoolMenuAnchor(e.currentTarget)}
                sx={{
                  color: 'inherit',
                  border: 0,
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <SchoolIcon fontSize="small" />
                {selectedSchool?.name ?? 'School'}
                <ArrowDropDownIcon fontSize="small" />
              </Typography>
              <Menu
                anchorEl={schoolMenuAnchor}
                open={Boolean(schoolMenuAnchor)}
                onClose={() => setSchoolMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {schools.map((s) => (
                  <MenuItem
                    key={s._id}
                    onClick={() => {
                      setSelectedSchoolId(s._id);
                      setSchoolMenuAnchor(null);
                      queryClient.invalidateQueries('parent-students');
                      queryClient.invalidateQueries(['dispersals']);
                    }}
                    selected={s._id === selectedSchoolId}
                  >
                    {s.name}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: 0 }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              mt: 0,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        {schoolReadOnly ? (
          <Alert severity="warning" sx={{ borderRadius: 0, flexShrink: 0 }}>
            This school account is inactive. You can browse the portal, but changes are not allowed. Please contact your administrator.
          </Alert>
        ) : null}
        <Box sx={{ flex: 1, py: 3, px: { xs: 2, sm: 3 }, width: '100%' }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
              {breadcrumbLabels.map((b, i) =>
                i < breadcrumbLabels.length - 1 ? (
                  <Typography key={b.path} component="button" variant="body2" color="inherit" onClick={() => navigate(b.path)} sx={{ border: 0, background: 'none', cursor: 'pointer', p: 0, '&:hover': { textDecoration: 'underline' } }}>
                    {b.label}
                  </Typography>
                ) : (
                  <Typography key={b.path} variant="body2" color="text.primary" fontWeight={600}>
                    {b.label}
                  </Typography>
                )
              )}
            </Breadcrumbs>
            <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
